import type { MelodyChordLane, MelodyLaneChord, MelodyMeter } from '@tunasong/schemas'
import { barBeats, feltBeat, GRID_TOLERANCE } from './grid.js'

/**
 * A melody's chord lane: when each chord sounds under it (melody toolkit plan M1; spec §4.2, §8, §10).
 *
 * Chord charts carry no timing, so the timing lives with the melody (§14.1). Inside a section the chart
 * names the chords and the melody keeps only when they sound, keyed by each chord element's id; outside a
 * section the stored chords are the lane. Pure, so every rule is tested without a screen.
 */

/** A chord as the section's chart holds it: the element's id, and its name */
export interface ChartChord {
  id: string
  symbol: string
}

const emptyLane: MelodyChordLane = { isConfirmed: false, chords: [] }

/**
 * The lane the singer sees. `chart` is the section's chords, or null outside a section.
 *
 * Each chart chord with stored timing keeps it. A chord with none follows the one before it and lasts a
 * bar, which is the guess (§4.2) and also the rule for chords added to the chart later (§8). A new chord
 * between two stored ones fills the gap up to the next, at most a bar; when there is no room, it takes its
 * bar and the chords after it follow on, so the lane never overlaps. Stored chords the chart no longer has
 * drop out.
 */
export const resolveChordLane = (
  chart: ChartChord[] | null,
  stored: MelodyChordLane | undefined,
  meter: MelodyMeter
): MelodyChordLane => {
  if (chart === null) {
    return stored ?? emptyLane
  }
  const bar = barBeats(meter)
  const step = feltBeat(meter)
  const byId = new Map((stored?.chords ?? []).flatMap(chord => (chord.chartId ? [[chord.chartId, chord]] : [])))
  const chords: MelodyLaneChord[] = []
  let cursor = 0
  let shift = 0
  chart.forEach((chord, index) => {
    const kept = byId.get(chord.id)
    if (kept) {
      const onset = Math.max(kept.onset + shift, cursor)
      shift = onset - kept.onset
      chords.push({ symbol: chord.symbol, onset, duration: kept.duration, chartId: chord.id })
      cursor = onset + kept.duration
      return
    }
    const next = chart.slice(index + 1).find(later => byId.has(later.id))
    const nextKept = next ? byId.get(next.id) : undefined
    const gap = nextKept ? nextKept.onset + shift - cursor : bar
    const duration = gap >= step - GRID_TOLERANCE ? Math.min(bar, gap) : bar
    chords.push({ symbol: chord.symbol, onset: cursor, duration, chartId: chord.id })
    cursor += duration
  })
  return { isConfirmed: stored?.isConfirmed ?? false, chords }
}

/**
 * Move chord `index`'s end edge by `beats` felt beats (§9: "Earlier by a beat"; §10: Alt+←/→). When the
 * next chord starts at that edge, its start moves with it. Neither chord gets shorter than a beat, so the
 * edge never passes the next chord's end. Moving an edge confirms the lane (§4.2).
 */
export const moveEdge = (lane: MelodyChordLane, index: number, beats: number, meter: MelodyMeter): MelodyChordLane => {
  const chord = lane.chords[index]
  if (!chord) {
    return lane
  }
  const step = feltBeat(meter)
  const end = chord.onset + chord.duration
  const next = lane.chords[index + 1]
  const isJoined = next !== undefined && Math.abs(next.onset - end) < GRID_TOLERANCE
  const max = next ? (isJoined ? next.onset + next.duration - step : next.onset) : Infinity
  const moved = Math.min(max, Math.max(chord.onset + step, end + beats * step))
  const chords = lane.chords.map((entry, at) => {
    if (at === index) {
      return { ...entry, duration: moved - entry.onset }
    }
    if (at === index + 1 && isJoined) {
      return { ...entry, onset: moved, duration: entry.onset + entry.duration - moved }
    }
    return entry
  })
  return { isConfirmed: true, chords }
}

/** The chord sounding at `beat`, or null where none does */
export const chordAt = (lane: MelodyChordLane, beat: number): MelodyLaneChord | null =>
  lane.chords.find(chord => beat >= chord.onset && beat < chord.onset + chord.duration) ?? null

/** Where the lane's last chord ends, in beats */
export const laneEnd = (lane: MelodyChordLane): number =>
  lane.chords.reduce((end, chord) => Math.max(end, chord.onset + chord.duration), 0)

/** Bar and felt beat of a position, both counted from 1 */
const position = (beats: number, meter: MelodyMeter) => {
  const bar = barBeats(meter)
  const barIndex = Math.floor(beats / bar + GRID_TOLERANCE)
  const beat = (beats - barIndex * bar) / feltBeat(meter) + 1
  return { bar: barIndex + 1, beat: Number(beat.toFixed(2)) }
}

const isOnBarLine = (beats: number, meter: MelodyMeter) => {
  const offset = beats % barBeats(meter)
  return offset < GRID_TOLERANCE || barBeats(meter) - offset < GRID_TOLERANCE
}

/** The last felt beat a chord sounds on */
const lastBeat = (chord: Pick<MelodyLaneChord, 'onset' | 'duration'>, meter: MelodyMeter) =>
  Math.max(chord.onset, chord.onset + chord.duration - feltBeat(meter))

type Timed = Pick<MelodyLaneChord, 'symbol' | 'onset' | 'duration'>

/**
 * A lane chord's accessible name (Joni's M1 amendments, approved by the user 2026-10-23): "G, bar 3",
 * "G, bars 3 to 4", or "Am, bar 2 beat 2 to bar 2 beat 4" when it starts or ends mid-bar. A guessed
 * timing says so.
 */
export const laneChordLabel = (chord: Timed, meter: MelodyMeter, isConfirmed: boolean): string => {
  const from = position(chord.onset, meter)
  const to = position(lastBeat(chord, meter), meter)
  const isWholeBars = isOnBarLine(chord.onset, meter) && isOnBarLine(chord.onset + chord.duration, meter)
  const span = isWholeBars
    ? from.bar === to.bar
      ? `bar ${from.bar}`
      : `bars ${from.bar} to ${to.bar}`
    : `bar ${from.bar} beat ${from.beat} to bar ${to.bar} beat ${to.beat}`
  return `${chord.symbol}, ${span}${isConfirmed ? '' : ', timing guessed'}`
}

/** The end-edge handle's name, so its position is announced as it moves: "End of G, bar 3 beat 4" */
export const laneEdgeLabel = (chord: Timed, meter: MelodyMeter): string => {
  const at = position(lastBeat(chord, meter), meter)
  return `End of ${chord.symbol}, bar ${at.bar} beat ${at.beat}`
}
